import React, { useContext, useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { AlertTriangle, ArrowRight } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import ReactSelector from "../common/CommonReactSelector";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import {
  autoMatchColumns,
  detectSerialHeader,
  findTargetConflicts,
  initialTargets,
  otherColumns,
  reservedHeaders,
} from "./inventoryUploadColumns";

// Same look and flow as the task import's ColumnMap (src/pages/task/ColumnMap.js):
// Upload File -> Mandatory Fields -> Other Fields, submitting from the last step.
// Mandatory Fields picks the serial column (or, for seals, the From / To
// columns); Other Fields lists every remaining sheet column and lets the operator
// choose which inventory field each one feeds.

const FOOTER_ROW_CLASS =
  "-mx-6 -mb-5 mt-5 flex items-center justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4";
const FOOTER_BUTTON_CLASS = "!h-10 !min-h-[40px] !max-h-10 rounded-lg !px-5";

const Stepper = ({ steps, currentStep, ariaLabel }) => (
  <ol data-testid="inventory-column-map-stepper" className="mb-5 flex w-full items-start" aria-label={ariaLabel}>
    {steps.map((step, index) => {
      const isCompleted = currentStep > step.id;
      const isActive = currentStep === step.id;
      const isLast = index === steps.length - 1;
      return (
        <li key={step.id} className={`flex min-w-0 items-start ${isLast ? "flex-none" : "flex-1"}`}>
          <div className="flex w-[72px] shrink-0 flex-col items-center">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all ${
                isCompleted
                  ? "bg-[#45B369] text-white"
                  : isActive
                    ? "bg-[#3F63EA] text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
              aria-current={isActive ? "step" : undefined}
            >
              {isCompleted ? <FaCheck className="text-xs" /> : step.id}
            </div>
            <p
              className={`mt-2 whitespace-nowrap text-center text-xs font-medium leading-tight ${
                isActive || isCompleted ? "text-slate-700" : "text-slate-400"
              }`}
            >
              {step.label}
            </p>
          </div>
          {!isLast && (
            <div
              className={`mx-1 mt-3.5 h-px min-w-[12px] flex-1 ${isCompleted ? "bg-[#45B369]" : "bg-[#E5E7EB]"}`}
              aria-hidden="true"
            />
          )}
        </li>
      );
    })}
  </ol>
);

const selectProps = {
  isSearchable: true,
  height: "36px",
  borderRadius: "8px",
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  menuPosition: "fixed",
  menuPlacement: "auto",
  maxMenuHeight: "200px",
};

/**
 * Maps the sheet's header row onto the inventory fields of the selected item type.
 *
 * The mapping state is `{ serial, required, targets }` -- see inventoryUploadColumns.js.
 *
 * @param {Object}   props.fields          `{ mandatory: [{key,label}], optional: [...] }`
 * @param {string[]} props.headers         Header row of the picked sheet.
 * @param {Object}   props.initialMappings State from a previous open for this sheet.
 * @param {Function} props.onSubmit        Receives the final `{ serial, required, targets }`.
 * @param {Function} props.onBack          Leaves the modal keeping the current state.
 */
export const InventoryColumnMapModal = ({
  open,
  fields,
  headers,
  initialMappings,
  itemType,
  itemTypeLabel,
  fileName,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const isRtl = isRTL(langMode);
  const [step, setStep] = useState(1);
  const [serial, setSerial] = useState("");
  const [required, setRequired] = useState({});
  const [targets, setTargets] = useState({});

  // Meters, modems and SIMs have one serial column; a seal sheet has no serial and
  // required From / To range columns instead.
  const serialField = fields.mandatory.find((field) => field.key === "identifier");
  const requiredFields = fields.mandatory.filter((field) => field.key !== "identifier");
  const detectedSerial = serialField ? detectSerialHeader(itemType, headers) : "";
  const effectiveSerial = serial || detectedSerial;
  const reserved = (nextSerial, nextRequired) =>
    reservedHeaders({ serial: nextSerial, required: nextRequired }, itemType, headers);
  const columns = otherColumns(headers, reserved(serial, required));

  // Every open starts on the mandatory step, keeping whatever was chosen before for
  // this sheet and auto-matching the rest by header name.
  useEffect(() => {
    if (!open) return;
    const previous = initialMappings || {};
    const matched = autoMatchColumns(fields.mandatory, headers, {
      ...(previous.required || {}),
      ...(previous.serial ? { identifier: previous.serial } : {}),
    });
    const nextSerial = serialField ? matched.identifier || "" : "";
    const nextRequired = Object.fromEntries(
      requiredFields.map((field) => [field.key, matched[field.key] || ""])
    );
    setStep(1);
    setSerial(nextSerial);
    setRequired(nextRequired);
    setTargets(
      initialTargets(
        fields.optional,
        otherColumns(headers, reserved(nextSerial, nextRequired)),
        previous.targets || {}
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Picking another serial column hands the old one back to Other Fields.
  const changeSerial = (header) => {
    setSerial(header);
    setTargets((prev) =>
      initialTargets(fields.optional, otherColumns(headers, reserved(header, required)), prev)
    );
  };

  const changeRequired = (fieldKey, header) => {
    const nextRequired = { ...required, [fieldKey]: header };
    setRequired(nextRequired);
    setTargets((prev) =>
      initialTargets(fields.optional, otherColumns(headers, reserved(serial, nextRequired)), prev)
    );
  };

  const serialMapped =
    (!serialField || Boolean(effectiveSerial)) &&
    requiredFields.every((field) => Boolean(required[field.key]));
  const state = { serial, required, targets };
  const conflicts = findTargetConflicts(state, itemType, headers);
  const canSubmit = serialMapped && conflicts.length === 0;
  const hasOtherColumns = columns.length > 0;

  const fieldLabel = (target) =>
    [...fields.mandatory, ...fields.optional].find((field) => field.key === target)?.label || target;
  const conflictMessage = (conflict) =>
    (t.inventoryImportMapConflict || "")
      .replace("{a}", conflict.header)
      .replace("{b}", conflict.other)
      .replace("{target}", fieldLabel(conflict.target));

  const serialHint =
    detectedSerial && (!serial || serial === detectedSerial)
      ? (t.inventoryImportSerialDetected || "").replace("{header}", detectedSerial)
      : (t.inventoryImportSerialAccepted || "").replace(
          "{names}",
          (serialField?.aliases || []).join(", ")
        );

  // An inventory field already chosen for one column is hidden from the others; the
  // column's own name is always offered first as the default.
  const targetOptionsFor = (header) => {
    const taken = new Set(
      Object.entries(targets)
        .filter(([other, target]) => other !== header && columns.includes(other) && target)
        .map(([, target]) => target)
    );
    return [
      { value: header, label: (t.inventoryImportKeepColumnName || "").replace("{header}", header) },
      ...fields.optional
        .filter((field) => !taken.has(field.key))
        .map((field) => ({ value: field.key, label: field.label })),
    ];
  };

  const setTarget = (header, target) =>
    setTargets((prev) => ({ ...prev, [header]: target || header }));

  const submit = () => {
    if (!serialMapped) {
      toast.error(t.pleasefillallmandtoryfield);
      return;
    }
    if (conflicts.length > 0) {
      toast.error(conflictMessage(conflicts[0]));
      return;
    }
    onSubmit(state);
  };

  const next = () => {
    if (!serialMapped) {
      toast.error(t.pleasefillallmandtoryfield);
      return;
    }
    setStep(2);
  };

  const stepperSteps = [
    { id: 1, label: t.uploadfilelabel },
    { id: 2, label: `${t.Mandatory} ${t.Fields}` },
    ...(hasOtherColumns ? [{ id: 3, label: `${t.Other} ${t.Fields}` }] : []),
  ];

  if (!open) return null;

  const themeClass = theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";
  const mappedToFieldCount = columns.filter((header) => targets[header] && targets[header] !== header).length;

  const conflictBox = conflicts.length > 0 && (
    <div
      className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700"
      role="alert"
      data-testid="inventory-column-map-conflicts"
    >
      <AlertTriangle size={15} className="mt-0.5 shrink-0" />
      <ul className="space-y-1">
        {conflicts.map((conflict) => (
          <li key={`${conflict.header}|${conflict.other}`}>{conflictMessage(conflict)}</li>
        ))}
      </ul>
    </div>
  );

  const serialOptions = headers.map((header) => ({ value: header, label: header }));
  // A header already used for one range column is not offered for the others.
  const requiredOptionsFor = (fieldKey) => {
    const taken = new Set(
      Object.entries(required)
        .filter(([key, header]) => key !== fieldKey && header)
        .map(([, header]) => header)
    );
    return serialOptions.filter((option) => !taken.has(option.value));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inventory-column-map-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        data-testid="inventory-column-map-modal"
        className={`relative flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${
          isRtl ? "text-right" : "text-left"
        } ${themeClass}`}
      >
        <div className="border-b border-[#E5E7EB] bg-white px-6 py-5 pe-14">
          <h2 id="inventory-column-map-title" className="text-base font-semibold text-[#1E293B]">
            {t.inventoryImportMapTitle}
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">{t.mapyourfilecolumns}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {itemTypeLabel && (
              <span className="rounded-md bg-blue-50 px-2 py-0.5 font-semibold text-[#236DB4]">
                {t.inventoryImportItemType}: {itemTypeLabel}
              </span>
            )}
            {fileName && (
              <span className="max-w-[280px] truncate rounded-md bg-slate-100 px-2 py-0.5 text-slate-600" title={fileName}>
                {fileName}
              </span>
            )}
          </div>
          <ModalCloseButton
            onClick={onClose}
            className={`absolute top-4 border-0 bg-transparent shadow-none hover:border-0 ${isRtl ? "left-4" : "right-4"}`}
            ariaLabel={t.cancelandclose}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <Stepper steps={stepperSteps} currentStep={step + 1} ariaLabel={t.columnmappingsteps} />

          {step === 1 && (
            <section data-testid="inventory-column-map-mandatory">
              <h3 className="text-sm font-semibold text-[#B91C1C]">
                {t.mandatoryfield} <span className="text-[#EF4444]">*</span>
              </h3>
              <p className="mt-1 text-xs text-[#6B7280]">{t.pleasefillallmandtoryfield}</p>
              {serialField && (
                <div className="mt-3 flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-[#374151]">
                    {serialField.label} - {t.primarykeyvalue}
                  </label>
                  <p className="-mt-1 text-xs text-slate-400">{serialHint}</p>
                  <ReactSelector
                    {...selectProps}
                    value={serialOptions.find((option) => option.value === effectiveSerial) || null}
                    options={serialOptions}
                    onChange={(option) => changeSerial(option?.value ?? "")}
                    placeholder={t.selectavalue}
                    isClearable
                    dataTestId="inventory-column-map-select-identifier"
                  />
                </div>
              )}
              {requiredFields.length > 0 && (
                <p className="mt-3 text-xs text-slate-400">{t.inventoryImportSealColumns}</p>
              )}
              {requiredFields.map((field) => {
                const options = requiredOptionsFor(field.key);
                return (
                  <div key={field.key} className="mt-3 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#374151]">
                      {field.label} <span className="text-[#EF4444]">*</span>
                    </label>
                    <ReactSelector
                      {...selectProps}
                      value={options.find((option) => option.value === required[field.key]) || null}
                      options={options}
                      onChange={(option) => changeRequired(field.key, option?.value ?? "")}
                      placeholder={t.selectavalue}
                      isClearable
                      dataTestId={`inventory-column-map-select-${field.key}`}
                    />
                  </div>
                );
              })}
              {conflictBox}
              <div className={FOOTER_ROW_CLASS}>
                <CommonButton colorIntent="primary"
                  onClick={() => onBack(state)}
                  variant="outline"
                  theme={theme}
                  className={FOOTER_BUTTON_CLASS}
                  tooltipText={t.gobacktopreviousstep}
                  tooltipPosition="top"
                  data-testid="inventory-column-map-back-to-upload"
                >
                  {t.Back}
                </CommonButton>
                <CommonButton colorIntent={hasOtherColumns ? "primary" : "success"}
                  onClick={hasOtherColumns ? next : submit}
                  variant={hasOtherColumns ? "primary" : "success"}
                  theme={theme}
                  disabled={hasOtherColumns ? !serialMapped : !canSubmit}
                  className={FOOTER_BUTTON_CLASS}
                  tooltipText={
                    !serialMapped
                      ? t.pleasefillallmandtoryfield
                      : hasOtherColumns
                        ? t.continuetonextstep
                        : t.submitmappeddataandstartupload
                  }
                  tooltipPosition="top"
                  data-testid={hasOtherColumns ? "inventory-column-map-next" : "inventory-column-map-submit"}
                >
                  {hasOtherColumns ? t.next : t.submit}
                </CommonButton>
              </div>
            </section>
          )}

          {step === 2 && hasOtherColumns && (
            <section data-testid="inventory-column-map-optional">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-[#111827]">
                  {t.Other} {t.Fields}
                </h3>
                <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-[#236DB4]">
                  {(t.inventoryImportColumnsMappedCount || "")
                    .replace("{mapped}", mappedToFieldCount)
                    .replace("{total}", columns.length)}
                </span>
              </div>
              <p className="mt-1 text-xs text-[#6B7280]">{t.inventoryImportOtherColumnsHint}</p>

              <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <span>{t.inventoryImportExcelColumn}</span>
                  <span className="w-4" aria-hidden="true" />
                  <span>{t.inventoryImportInventoryField}</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {columns.map((header) => {
                    const options = targetOptionsFor(header);
                    const value = targets[header] || header;
                    const clashing = conflicts.some(
                      (conflict) => conflict.header === header || conflict.other === header
                    );
                    return (
                      <li
                        key={header}
                        className={`grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-3 py-2 ${
                          clashing ? "bg-red-50/60" : ""
                        }`}
                        data-testid="inventory-column-map-row"
                      >
                        <span className="truncate text-sm font-medium text-slate-700" title={header}>
                          {header}
                        </span>
                        <ArrowRight size={14} className={`text-slate-300 ${isRtl ? "rotate-180" : ""}`} aria-hidden="true" />
                        <ReactSelector
                          {...selectProps}
                          value={options.find((option) => option.value === value) || options[0]}
                          options={options}
                          onChange={(option) => setTarget(header, option?.value)}
                          dataTestId={`inventory-column-map-target-${header}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
              {conflictBox}
              <div className={FOOTER_ROW_CLASS}>
                <CommonButton colorIntent="primary"
                  onClick={() => setStep(1)}
                  variant="outline"
                  theme={theme}
                  className={FOOTER_BUTTON_CLASS}
                  tooltipText={t.gobacktomapmandotoryfield || t.gobacktopreviousstep}
                  tooltipPosition="top"
                  data-testid="inventory-column-map-back"
                >
                  {t.Back}
                </CommonButton>
                <CommonButton colorIntent="success"
                  onClick={submit}
                  variant="success"
                  disabled={!canSubmit}
                  className={FOOTER_BUTTON_CLASS}
                  tooltipText={t.submitmappeddataandstartupload}
                  tooltipPosition="left"
                  data-testid="inventory-column-map-submit"
                >
                  {t.submit}
                </CommonButton>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryColumnMapModal;
